import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  showBanner: boolean;
  install: () => Promise<void>;
  dismissBanner: () => void;
  showBannerManually: () => void;
  resetCooldown: () => void;
  forcePWADetection: () => void;
  deferredPrompt: BeforeInstallPromptEvent | null;
}

// ChromeOS Detection
const isChromeOS = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return (
    userAgent.includes("cros") ||
    userAgent.includes("chromeos") ||
    (userAgent.includes("chrome") &&
      userAgent.includes("linux") &&
      userAgent.includes("armv7l")) ||
    navigator.platform.toLowerCase().includes("cros") ||
    /\bCrOS\b/.test(navigator.userAgent)
  );
};

// Only show banner on ChromeOS web browsers
const shouldShowOnPlatform = (): boolean => {
  const isChromOSDevice = isChromeOS();
  const isWebBrowser = !window.electron && !window.circadia;
  return isChromOSDevice && isWebBrowser;
};

export const usePWAInstall = (): PWAInstallState => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(false);

  // Check if app is already installed
  const checkIfInstalled = () => {
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
    const isIOSStandalone = (navigator as any).standalone === true;
    return isStandalone || isIOSStandalone;
  };

  useEffect(() => {
    // Check if already installed
    setIsInstalled(checkIfInstalled());

    // Early return if not on ChromeOS
    if (!shouldShowOnPlatform()) {
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      const event = e as BeforeInstallPromptEvent;

      // Prevent the mini-infobar from appearing
      e.preventDefault();

      // Save the event for later use
      setDeferredPrompt(event);
      setIsInstallable(true);

      // Show banner immediately if app isn't installed and not dismissed in this session
      if (!checkIfInstalled() && !sessionDismissed) {
        setShowBanner(true);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setShowBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [sessionDismissed]);

  const install = async (): Promise<void> => {
    if (!deferredPrompt) {
      console.warn("⚠️ [PWA Install] No install prompt available");
      return;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setIsInstallable(false);
      setShowBanner(false);
    } catch (error) {
      console.error("❌ [PWA Install] Install failed:", error);
    }
  };

  const dismissBanner = (): void => {
    setShowBanner(false);
    setSessionDismissed(true);
  };

  const showBannerManually = (): void => {
    if (!shouldShowOnPlatform()) {
      console.warn(
        "⚠️ [PWA Install] Cannot show banner - not on ChromeOS device"
      );
      return;
    }

    if (!isInstalled && deferredPrompt) {
      setShowBanner(true);
      setIsInstallable(true);
      setSessionDismissed(false);
    } else {
      console.warn(
        "⚠️ [PWA Install] Cannot show banner manually - app installed or no prompt available"
      );
    }
  };

  const resetCooldown = (): void => {
    setSessionDismissed(false);
  };

  // Force PWA detection for SPA navigation
  const forcePWADetection = (): void => {
    if (!shouldShowOnPlatform()) {
      return;
    }

    // Method 1: Dispatch storage event to potentially trigger Chrome's PWA check
    try {
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "pwa-detection-trigger",
          newValue: Date.now().toString(),
          url: window.location.href,
        })
      );
    } catch {
      // Continue
    }

    // Method 2: Slight URL manipulation to trigger re-evaluation
    try {
      const currentUrl = new URL(window.location.href);
      const hasFragment = currentUrl.hash.includes("#pwa-check");

      if (!hasFragment) {
        // Add temporary fragment
        window.history.replaceState(
          {},
          "",
          window.location.href + "#pwa-check"
        );
        setTimeout(() => {
          // Remove temporary fragment
          window.history.replaceState(
            {},
            "",
            window.location.href.replace("#pwa-check", "")
          );
        }, 100);
      }
    } catch {
      // continue
    }

    // Method 3: Force service worker message
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: "PWA_DETECTION_TRIGGER",
        timestamp: Date.now(),
      });
    }
  };

  return {
    isInstallable,
    isInstalled,
    showBanner,
    install,
    dismissBanner,
    showBannerManually,
    resetCooldown,
    forcePWADetection,
    deferredPrompt,
  };
};
